import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { cn } from '@/utils/utils';
import { useAtom } from 'jotai';
import {
  ChevronRight,
  CircleHelp,
  FileText,
  FolderOpen,
  LinkIcon,
  Plus,
  PlusIcon,
  ScanBarcode,
  Shapes,
  UploadIcon,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { CreateItems } from '../AppLanding/buttons/create';
import { IdImportForm, UrlImportForm } from '../AppLanding/buttons/url-import';
import { Button } from '../Button/Button';
import useIngestMultipleFiles, {
  SUPPORTED_EXTENSIONS,
} from '../Ingest/useIngestMultipleFiles';
import { zoteroModalAtom } from '../Layouts/site';
import { LoadingSpinner } from '../Loading';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { Icon } from '../ui/Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover';
import { ReusableTooltip } from '../ui/ReusableTooltip';
const MobilePlusMenu = ({
  handleFileOptionClick,
  handleFolderOptionClick,
  handleZoteroOptionClick,
  openMenu,
  handleMenuOpenChange,
  copyText,
  routingToZotero,
}: {
  handleFileOptionClick: () => void;
  handleFolderOptionClick: () => void;
  handleZoteroOptionClick: () => void;
  openMenu: boolean;
  handleMenuOpenChange: (open: boolean) => void;
  copyText?: string;
  routingToZotero: boolean;
}) => {
  const [openPopover, setOpenPopover] = useState<string | null>(null);

  const { track } = useAnalytics();
  const handlePopoverChange = (name: string, isOpen: boolean) => {
    if (isOpen) {
      setOpenPopover(name);
    } else if (openPopover === name) {
      setOpenPopover(null);
    }
  };
  const [isLoading, setIsLoading] = useState(false);

  return (
    <DropdownMenu
      open={openMenu}
      onOpenChange={(open) => {
        handleMenuOpenChange(open);
        if (!open) {
          setOpenPopover(null);
        }
      }}
    >
      <DropdownMenuTrigger disabled={isLoading} asChild>
        <Button
          size={copyText ? 'smaller' : 'icon'}
          isLoading={isLoading}
          variant={copyText ? 'default' : 'secondary'}
          className={cn(copyText && '!bg-primary')}
        >
          <Icon as={PlusIcon} />
          {copyText}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[218px] p-0">
        <div className="p-0.5">
          <Popover
            open={openPopover === 'upload'}
            onOpenChange={(open) => handlePopoverChange('upload', open)}
          >
            <PopoverTrigger asChild>
              <button className="relative flex w-full cursor-default gap-2 select-none items-center rounded-[8px] px-1.5 py-1 text-sm outline-none transition-colors hover:bg-popover-hover">
                <Icon as={UploadIcon} />
                <span className="flex-1 text-left">Upload files</span>
                <Icon as={ChevronRight} className="ml-auto h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              sideOffset={4}
              className="p-0.5 z-[100]"
            >
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={handleFileOptionClick}
                  className="flex w-full items-center gap-2 px-1.5 py-1 text-sm rounded-[8px] hover:bg-popover-hover"
                >
                  <Icon as={FileText} />
                  <span>File</span>
                </button>
                <button
                  onClick={handleFolderOptionClick}
                  className="flex w-full items-center gap-2 px-1.5 py-1 text-sm rounded-[8px] hover:bg-popover-hover"
                >
                  <Icon as={FolderOpen} />
                  <span>Folder</span>
                </button>
                <button
                  onClick={handleZoteroOptionClick}
                  className="flex w-full items-center gap-2 px-1.5 py-1 text-sm rounded-[8px] hover:bg-popover-hover"
                >
                  {routingToZotero ? (
                    <LoadingSpinner variant="small" />
                  ) : (
                    <Icon as={Shapes} />
                  )}
                  <span>Zotero</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>

          <Popover
          // open={openPopover === 'url'}
          // onOpenChange={(open) => handlePopoverChange('url', open)}
          >
            <PopoverTrigger asChild>
              <button className="relative flex w-full cursor-default gap-2 select-none items-center rounded-[8px] px-1.5 py-1 text-sm outline-none transition-colors hover:bg-popover-hover">
                <Icon as={LinkIcon} />
                <span className="flex-1 text-left">URL / ID import</span>
                <Icon as={ChevronRight} className="ml-auto h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              sideOffset={4}
              className="w-[224px] p-0 z-[100]"
            >
              <Popover
                open={openPopover === 'id'}
                onOpenChange={(open) => handlePopoverChange('id', open)}
              >
                <PopoverTrigger asChild>
                  <button className="relative flex w-full cursor-default gap-2 select-none items-center rounded-[8px] px-1.5 py-1 text-sm outline-none transition-colors hover:bg-popover-hover">
                    <Icon as={ScanBarcode} />
                    <span className="flex-1 text-left">ID</span>
                    <Icon as={ChevronRight} className="ml-auto h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  className="w-[260px] min-h-[122px] p-0 z-[100]"
                  onMouseEnter={() => handlePopoverChange('id', true)}
                >
                  <IdImportForm
                    setOpenMenu={() => {
                      setOpenPopover(null);
                      handleMenuOpenChange(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
              <Popover
                open={openPopover === 'url'}
                onOpenChange={(open) => handlePopoverChange('url', open)}
              >
                <PopoverTrigger asChild>
                  <button className="relative flex w-full cursor-default gap-2 select-none items-center rounded-[8px] px-1.5 py-1 text-sm outline-none transition-colors hover:bg-popover-hover">
                    <Icon as={LinkIcon} />
                    <span className="flex-1 text-left">URL</span>
                    <Icon as={ChevronRight} className="ml-auto h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  className="w-[260px] min-h-[122px] p-0 z-[100]"
                  onMouseEnter={() => handlePopoverChange('url', true)}
                >
                  <UrlImportForm
                    setOpenMenu={() => {
                      setOpenPopover(null);
                      handleMenuOpenChange(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </PopoverContent>
          </Popover>

          <Popover
            open={openPopover === 'create'}
            onOpenChange={(open) => handlePopoverChange('create', open)}
          >
            <PopoverTrigger asChild>
              <button className="relative flex w-full cursor-default gap-2 select-none items-center rounded-[8px] px-1.5 py-1 text-sm outline-none transition-colors hover:bg-popover-hover">
                <Icon as={Plus} />
                <span className="flex-1 text-left">Create</span>
                <Icon as={ChevronRight} className="ml-auto h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              sideOffset={4}
              className="w-[224px] p-0.5 z-[100]"
            >
              <CreateItems setIsLoading={setIsLoading} />
            </PopoverContent>
          </Popover>
        </div>

        <div className="h-[1px] bg-border" />
        <div className="p-0.5">
          <DropdownMenuItem className="text-textGray4">
            <Link
              href="/docs/import/file-types"
              target="_blank"
              onClick={() => {
                track('plus_menu_button_clicked', {
                  button: 'learn_file_types',
                });
              }}
              className="flex w-full flex-row gap-2 items-center"
            >
              <Icon as={CircleHelp} />
              Learn about file types
            </Link>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const DesktopPlusMenu = ({
  handleFileOptionClick,
  handleFolderOptionClick,
  handleZoteroOptionClick,
  openMenu,
  handleMenuOpenChange,
  copyText,
  routingToZotero,
}: {
  handleFileOptionClick: () => void;
  handleFolderOptionClick: () => void;
  handleZoteroOptionClick: () => void;
  openMenu: boolean;
  handleMenuOpenChange: (open: boolean) => void;
  copyText?: string;
  routingToZotero: boolean;
}) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { track } = useAnalytics();

  const handleMouseEnter = (item: string) => {
    setHoveredItem(item);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  return (
    <DropdownMenu open={openMenu} onOpenChange={handleMenuOpenChange}>
      <DropdownMenuTrigger disabled={isLoading} asChild>
        <Button
          size={copyText ? 'smaller' : 'icon'}
          isLoading={isLoading}
          variant={copyText ? 'default' : 'secondary'}
          className={cn(copyText && '!bg-primary')}
        >
          <Icon as={PlusIcon} />
          {copyText}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[192px] p-0">
        <div className="p-0.5">
          <DropdownMenuSub
            open={hoveredItem === 'upload'}
            onOpenChange={() => {}}
          >
            <DropdownMenuSubTrigger
              onMouseEnter={() => handleMouseEnter('upload')}
              onMouseLeave={handleMouseLeave}
            >
              <Icon as={UploadIcon} />
              Upload
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                sideOffset={-4}
                className="w-[192px]"
                onMouseEnter={() => handleMouseEnter('upload')}
                onMouseLeave={handleMouseLeave}
              >
                <DropdownMenuItem onSelect={handleFileOptionClick}>
                  <div className="w-full">
                    <ReusableTooltip
                      content="Documents, images, audio and video"
                      align="start"
                    >
                      <div className="flex w-full flex-row justify-start items-center gap-2">
                        <Icon as={FileText} />
                        File
                      </div>
                    </ReusableTooltip>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleFolderOptionClick}>
                  <Icon as={FolderOpen} />
                  Folder
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleZoteroOptionClick}>
                  {routingToZotero ? (
                    <LoadingSpinner variant="small" />
                  ) : (
                    <Icon as={Shapes} />
                  )}
                  Zotero
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSub onOpenChange={() => {}}>
            <DropdownMenuSubTrigger
            // onMouseEnter={() => handleMouseEnter('url')}
            // onMouseLeave={handleMouseLeave}
            >
              <Icon as={LinkIcon} />
              Import
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                sideOffset={-4}
                className="w-[192px]"
                // onMouseEnter={() => handleMouseEnter('url')}
                // onMouseLeave={handleMouseLeave}
              >
                <DropdownMenuSub open={hoveredItem === 'id'}>
                  <DropdownMenuSubTrigger
                    onMouseEnter={() => handleMouseEnter('id')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Icon as={ScanBarcode} />
                    ID
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent
                      sideOffset={-4}
                      className="w-[272px] min-h-[122px]"
                      onMouseEnter={() => handleMouseEnter('id')}
                    >
                      <IdImportForm
                        setOpenMenu={handleMenuOpenChange}
                        landingPage
                      />
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                <DropdownMenuSub open={hoveredItem === 'url'}>
                  <DropdownMenuSubTrigger
                    onMouseEnter={() => handleMouseEnter('url')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Icon as={LinkIcon} />
                    URL
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent
                      sideOffset={-4}
                      className="w-[272px] min-h-[122px]"
                      onMouseEnter={() => handleMouseEnter('url')}
                    >
                      <UrlImportForm
                        setOpenMenu={handleMenuOpenChange}
                        landingPage
                      />
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSub
            open={hoveredItem === 'create'}
            onOpenChange={() => {}}
          >
            <DropdownMenuSubTrigger
              onMouseEnter={() => handleMouseEnter('create')}
              onMouseLeave={handleMouseLeave}
            >
              <Icon as={Plus} />
              Create
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                sideOffset={-4}
                className="w-[224px]"
                onMouseEnter={() => handleMouseEnter('create')}
                onMouseLeave={handleMouseLeave}
              >
                <CreateItems setIsLoading={setIsLoading} />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        </div>

        <div className="h-[1px] bg-border" />
        <div className="p-0.5">
          <DropdownMenuItem className="text-textGray4">
            <Link
              href="/docs/import/file-types"
              target="_blank"
              onClick={() => {
                track('plus_menu_button_clicked', {
                  button: 'learn_file_types',
                });
              }}
              className="flex w-full flex-row gap-2 items-center"
            >
              <Icon as={CircleHelp} />
              Learn about file types
            </Link>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PlusMenu = ({
  copyText,
}: {
  copyText?: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const { processFiles } = useIngestMultipleFiles();
  const [openMenu, setOpenMenu] = useState(false);
  const { track } = useAnalytics();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const router = useRouter();
  const [_, setShowZoteroModal] = useAtom(zoteroModalAtom);
  const [routingToZotero, setRoutingToZotero] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
      e.target.value = '';
    }
  };

  const handleFileOptionClick = () => {
    if (fileInputRef.current) {
      setTimeout(() => {
        fileInputRef.current?.click();
      }, 50);
      track('plus_menu_button_clicked', {
        button: 'upload_file',
      });
      setOpenMenu(false);
    }
  };

  const handleFolderOptionClick = () => {
    if (folderInputRef.current) {
      setTimeout(() => {
        folderInputRef.current?.click();
      }, 50);
      track('plus_menu_button_clicked', {
        button: 'upload_folder',
      });
      setOpenMenu(false);
    }
  };

  const handleMenuOpenChange = (open: boolean) => {
    setOpenMenu(open);
    if (open && copyText) {
      track('library_add_clicked');
    }
  };

  const handleZoteroOptionClick = () => {
    if (user?.zoteroKey && user?.zoteroId) {
      setShowZoteroModal(true);
    } else {
      track('zotero_connect', { from: 'plus_menu' });
      setRoutingToZotero(true);
      router.push(`/api/zotero/connect?userId=${user?.id}&step=init`);
    }
  };

  return (
    <>
      <input
        id="plus-menu-file-input"
        type="file"
        ref={fileInputRef}
        className="hidden"
        multiple
        accept={SUPPORTED_EXTENSIONS.join(',')}
        onChange={handleFileChange}
      />
      <input
        id="plus-menu-folder-input"
        type="file"
        ref={folderInputRef}
        className="hidden"
        multiple
        onChange={handleFileChange}
        // @ts-ignore - TypeScript doesn't know about these attributes
        webkitdirectory=""
        directory=""
      />

      {isMobile ? (
        <MobilePlusMenu
          handleFileOptionClick={handleFileOptionClick}
          handleFolderOptionClick={handleFolderOptionClick}
          openMenu={openMenu}
          handleMenuOpenChange={handleMenuOpenChange}
          copyText={copyText}
          handleZoteroOptionClick={handleZoteroOptionClick}
          routingToZotero={routingToZotero}
        />
      ) : (
        <DesktopPlusMenu
          handleFileOptionClick={handleFileOptionClick}
          handleFolderOptionClick={handleFolderOptionClick}
          openMenu={openMenu}
          handleMenuOpenChange={handleMenuOpenChange}
          copyText={copyText}
          handleZoteroOptionClick={handleZoteroOptionClick}
          routingToZotero={routingToZotero}
        />
      )}
    </>
  );
};

export default PlusMenu;
